import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, MessageCircle, ArrowRight, MapPin, Mail, Check } from "lucide-react";
import { SEO } from "../components/SEO";
import { Reveal, MaskLines, Chapter } from "../components/Reveal";
import { EditorialMarquee } from "../components/Marquee";
import { EnquiryForm } from "../components/EnquiryForm";
import {
  FIRM, WHATSAPP_URL, MAPS_EMBED_URL, MAPS_LINK, IMAGES,
  SERVICES, INDUSTRIES, TRUST_INDICATORS, PROCESS_STEPS, DISCLAIMER_TEXT,
} from "../data/content";

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 110]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, 60]);

  return (
    <div data-testid="home-page">
      <SEO
        title=""
        description="Neeraj Battan & Associates, Chartered Accountants in Kurukshetra, Haryana — income tax, GST, audit, accounting, company compliance and financial advisory services for individuals, businesses, startups and NRIs."
        path="/"
      />

      <section ref={heroRef} className="relative bg-navy text-white overflow-hidden" data-testid="hero-section">
        <div className="grain-overlay" />
        <div className="absolute -top-40 -right-40 w-[540px] h-[540px] rounded-full bg-gold/10 blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-36 pb-20 lg:pt-44 lg:pb-28">
          <div className="grid lg:grid-cols-12 gap-14 items-center">
            <motion.div style={{ y: textY }} className="lg:col-span-7">
              <Reveal y={10}>
                <p className="eyebrow mb-6" data-testid="hero-tagline">{FIRM.tagline}</p>
              </Reveal>
              <h1 className="font-serif font-bold leading-[1.08] tracking-tight text-4xl sm:text-5xl lg:text-6xl" data-testid="hero-heading">
                <MaskLines
                  lines={["Professional Chartered", "Accountancy & Financial"]}
                  lineClassName="text-white"
                />
                <MaskLines lines={["Advisory Services"]} lineClassName="text-gold italic" baseDelay={0.45} />
              </h1>
              <Reveal delay={0.7} className="mt-7 max-w-xl">
                <p className="text-base sm:text-lg text-slate-300 leading-relaxed" data-testid="hero-subtext">
                  {FIRM.name} provides reliable taxation, accounting, audit, compliance and financial advisory
                  services to individuals, businesses, startups and corporate clients.
                </p>
              </Reveal>
              <Reveal delay={0.85} className="mt-9 flex flex-wrap gap-3.5">
                <Link
                  to="/contact"
                  data-testid="hero-book-consultation-button"
                  className="inline-flex items-center gap-2 rounded-full bg-gold px-6 py-3.5 text-sm font-bold text-navy hover:bg-gold-bright transition-colors"
                >
                  Book a Consultation <ArrowRight size={16} />
                </Link>
                <a
                  href={`tel:${FIRM.phone1Raw}`}
                  data-testid="hero-call-now-button"
                  className="inline-flex items-center gap-2 rounded-full border border-white/30 px-6 py-3.5 text-sm font-semibold text-white hover:bg-white/10 transition-colors"
                >
                  <Phone size={16} className="text-gold" /> Call Now
                </a>
                <a
                  href={WHATSAPP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="hero-whatsapp-button"
                  className="inline-flex items-center gap-2 rounded-full border border-white/30 px-6 py-3.5 text-sm font-semibold text-white hover:bg-white/10 transition-colors"
                >
                  <MessageCircle size={16} className="text-gold" /> WhatsApp Us
                </a>
                <Link
                  to="/services"
                  data-testid="hero-explore-services-button"
                  className="inline-flex items-center gap-2 px-2 py-3.5 text-sm font-semibold text-gold hover:text-gold-bright transition-colors"
                >
                  Explore Our Services <ArrowRight size={15} />
                </Link>
              </Reveal>
            </motion.div>
            <div className="lg:col-span-5 hidden lg:block">
              <motion.div
                initial={{ opacity: 0, scale: 0.94 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 1.1, delay: 0.35, ease: [0.22, 1, 0.36, 1] }}
                className="relative"
              >
                <div className="absolute -inset-3 border border-gold/30 rounded-2xl translate-x-4 translate-y-4" />
                <motion.div style={{ y: imgY }} className="relative rounded-2xl overflow-hidden aspect-[4/5] shadow-2xl shadow-black/50">
                  <img src={IMAGES.hero} alt="Corporate finance advisory office" className="w-full h-full object-cover" loading="eager" />
                  <div className="absolute inset-0 bg-gradient-to-t from-navy/70 via-transparent to-transparent" />
                </motion.div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      <EditorialMarquee />

      <section className="py-20 sm:py-28 bg-background" data-testid="intro-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Chapter num="01" label="The Firm" />
          <div className="grid lg:grid-cols-2 gap-14 items-center">
            <Reveal>
              <h2 className="font-serif text-3xl sm:text-4xl font-semibold text-navy leading-tight tracking-tight">
                A practice built on integrity, accuracy and <span className="text-gold italic">confidentiality</span>.
              </h2>
              <p className="mt-6 text-slate-600 leading-relaxed">
                Neeraj Battan &amp; Associates is a Chartered Accountancy firm based in Kurukshetra, Haryana, led by
                CA Neeraj Battan. The firm serves individuals, businesses, startups, firms, companies, NRIs and other
                organisations with taxation, audit, accounting, compliance and financial advisory services.
              </p>
              <p className="mt-4 text-slate-600 leading-relaxed">
                Our approach rests on professional integrity and a clear understanding of each client's requirements —
                so that statutory responsibilities are met on time and financial decisions are made with confidence.
              </p>
              <Link
                to="/about"
                data-testid="intro-about-link"
                className="mt-7 inline-flex items-center gap-2 text-sm font-semibold text-navy border-b-2 border-gold pb-1 hover:text-gold transition-colors"
              >
                More About the Firm <ArrowRight size={15} />
              </Link>
            </Reveal>
            <Reveal delay={0.15} className="relative">
              <div className="rounded-2xl overflow-hidden aspect-[4/3] shadow-xl shadow-navy/10">
                <img src={IMAGES.boardroom} alt="Professional consultation at Neeraj Battan & Associates" className="w-full h-full object-cover" loading="lazy" />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-navy text-white rounded-xl px-6 py-5 shadow-xl hidden sm:block">
                <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-gold">Kurukshetra · Haryana</p>
                <p className="mt-1.5 font-serif text-lg font-semibold">Trusted Financial Guidance</p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="py-20 sm:py-28 bg-slate-100" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Chapter num="02" label="What We Do" />
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-12">
            <Reveal>
              <h2 className="font-serif text-3xl sm:text-4xl font-semibold text-navy tracking-tight">Major Professional Services</h2>
            </Reveal>
            <Reveal delay={0.1}>
              <Link to="/services" className="text-sm font-semibold text-gold hover:text-navy transition-colors inline-flex items-center gap-2" data-testid="services-view-all-link">
                View all services <ArrowRight size={15} />
              </Link>
            </Reveal>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {SERVICES.map((s, i) => (
              <Reveal key={s.slug} delay={(i % 3) * 0.08}>
                <Link
                  to={`/services#${s.slug}`}
                  data-testid={`service-card-${s.slug}`}
                  className="group block h-full rounded-xl border border-slate-200/80 bg-white p-7 hover:border-gold/60 hover:shadow-xl hover:shadow-navy/10 transition-all duration-300"
                >
                  <s.icon size={28} className="text-gold" strokeWidth={1.6} />
                  <h3 className="mt-5 font-serif text-xl font-semibold text-navy group-hover:text-gold transition-colors">{s.title}</h3>
                  <p className="mt-3 text-sm text-slate-500 leading-relaxed">{s.short}</p>
                  <span className="mt-5 inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-navy group-hover:text-gold transition-colors">
                    Learn more <ArrowRight size={13} />
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 sm:py-28 bg-navy text-white relative overflow-hidden" data-testid="why-choose-section">
        <div className="grain-overlay" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Chapter num="03" label="Why Choose Us" dark />
          <Reveal>
            <h2 className="font-serif text-3xl sm:text-4xl font-semibold tracking-tight mb-14 max-w-2xl">
              Professional standards you can rely on.
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 rounded-2xl overflow-hidden border border-white/10">
            {TRUST_INDICATORS.map((t, i) => (
              <Reveal key={t.title} delay={(i % 3) * 0.08} className="bg-navy">
                <div className="p-8 h-full hover:bg-navy-light transition-colors duration-300" data-testid={`trust-indicator-${i}`}>
                  <t.icon size={26} className="text-gold" strokeWidth={1.6} />
                  <h3 className="mt-4 font-semibold text-white">{t.title}</h3>
                  <p className="mt-2 text-sm text-slate-400 leading-relaxed">{t.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 sm:py-28 bg-background" data-testid="industries-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Chapter num="04" label="Who We Serve" />
          <div className="grid lg:grid-cols-3 gap-10 items-end mb-12">
            <Reveal className="lg:col-span-2">
              <h2 className="font-serif text-3xl sm:text-4xl font-semibold text-navy tracking-tight">Industries &amp; Clients Served</h2>
            </Reveal>
            <Reveal delay={0.1}>
              <Link to="/industries" className="text-sm font-semibold text-gold hover:text-navy transition-colors inline-flex items-center gap-2" data-testid="industries-view-all-link">
                All industries <ArrowRight size={15} />
              </Link>
            </Reveal>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {INDUSTRIES.slice(0, 8).map((ind, i) => (
              <Reveal key={ind.name} delay={(i % 4) * 0.06}>
                <div className="flex items-center gap-3 rounded-lg border border-slate-200 bg-white px-4 py-4 hover:border-gold/50 hover:shadow-md transition-all" data-testid={`industry-chip-${i}`}>
                  <ind.icon size={18} className="text-gold shrink-0" strokeWidth={1.8} />
                  <span className="text-sm font-medium text-slate-700">{ind.name}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 sm:py-28 bg-slate-100" data-testid="process-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Chapter num="05" label="The Process" />
          <Reveal>
            <h2 className="font-serif text-3xl sm:text-4xl font-semibold text-navy tracking-tight mb-14">A simple consultation process</h2>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {PROCESS_STEPS.map((p, i) => (
              <Reveal key={p.num} delay={i * 0.1}>
                <div className="relative pt-8 border-t-2 border-gold" data-testid={`process-step-${i}`}>
                  <span className="font-mono text-4xl text-gold/60 font-medium">{p.num}</span>
                  <h3 className="mt-3 font-serif text-xl font-semibold text-navy">{p.title}</h3>
                  <p className="mt-2.5 text-sm text-slate-600 leading-relaxed">{p.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section id="enquire" className="py-20 sm:py-28 bg-background" data-testid="enquiry-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Chapter num="06" label="Get In Touch" />
          <div className="grid lg:grid-cols-5 gap-12">
            <Reveal className="lg:col-span-2">
              <h2 className="font-serif text-3xl sm:text-4xl font-semibold text-navy tracking-tight">Request a Consultation</h2>
              <p className="mt-5 text-slate-600 leading-relaxed">
                Share your requirement and our office will review your enquiry and contact you shortly. For an
                immediate discussion, call or WhatsApp us directly.
              </p>
              <div className="mt-8 space-y-4">
                <a href={`tel:${FIRM.phone1Raw}`} className="flex items-center gap-4 group" data-testid="enquiry-side-phone">
                  <span className="w-11 h-11 rounded-full bg-navy flex items-center justify-center group-hover:bg-gold transition-colors">
                    <Phone size={18} className="text-white" />
                  </span>
                  <div>
                    <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Call</p>
                    <p className="font-semibold text-navy">{FIRM.phone1}</p>
                  </div>
                </a>
                <a href={`mailto:${FIRM.email}`} className="flex items-center gap-4 group" data-testid="enquiry-side-email">
                  <span className="w-11 h-11 rounded-full bg-navy flex items-center justify-center group-hover:bg-gold transition-colors">
                    <Mail size={18} className="text-white" />
                  </span>
                  <div>
                    <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Email</p>
                    <p className="font-semibold text-navy break-all">{FIRM.email}</p>
                  </div>
                </a>
                <div className="flex items-center gap-4">
                  <span className="w-11 h-11 rounded-full bg-navy flex items-center justify-center">
                    <MapPin size={18} className="text-white" />
                  </span>
                  <div>
                    <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Office</p>
                    <p className="font-semibold text-navy text-sm leading-snug">{FIRM.addressLines.join(", ")}</p>
                  </div>
                </div>
              </div>
            </Reveal>
            <Reveal delay={0.15} className="lg:col-span-3">
              <EnquiryForm />
            </Reveal>
          </div>
        </div>
      </section>

      <section className="pb-20 sm:pb-28 bg-background" data-testid="home-location-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="rounded-2xl overflow-hidden border border-slate-200 shadow-lg grid lg:grid-cols-2 bg-white">
              <div className="p-8 sm:p-12">
                <p className="eyebrow">Office Location</p>
                <h3 className="mt-4 font-serif text-2xl sm:text-3xl font-semibold text-navy">Visit Us in Kurukshetra</h3>
                <p className="mt-4 text-slate-600 leading-relaxed">{FIRM.addressLines.join(", ")}</p>
                <a
                  href={MAPS_LINK}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="home-directions-link"
                  className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-navy border-b-2 border-gold pb-1 hover:text-gold transition-colors"
                >
                  Get Directions <ArrowRight size={15} />
                </a>
              </div>
              <iframe
                title="Neeraj Battan & Associates office location map"
                src={MAPS_EMBED_URL}
                className="w-full h-72 lg:h-full min-h-[300px] border-0"
                loading="lazy"
                data-testid="home-map-embed"
              />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="pb-20 bg-background" data-testid="home-disclaimer-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="rounded-xl bg-slate-100 border border-slate-200 p-6 sm:p-8">
            <p className="text-xs font-mono uppercase tracking-[0.25em] text-gold mb-3">Disclaimer</p>
            <p className="text-xs sm:text-sm text-slate-500 leading-relaxed">{DISCLAIMER_TEXT}</p>
          </div>
        </div>
      </section>
    </div>
  );
}
